/**
 * Fetches the server's integration catalog (`GET /api/integrations`).
 * Catalog is what drives the Integrations UI — which integrations exist,
 * what they're called, what fields each one needs. Adding a new integration
 * on the server (sc-platform `PROVIDER_CATALOG`) makes it appear here
 * automatically with no client code change.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { IntegrationCatalogResponse, IntegrationDefinition } from '../../lib/types.js';

export const useIntegrationCatalog = (): UseQueryResult<IntegrationCatalogResponse, Error> =>
  useQuery<IntegrationCatalogResponse>({
    queryKey: queryKeys.integrationCatalog(),
    queryFn: () => apiRequest<IntegrationCatalogResponse>('/api/integrations/catalog'),
    // Catalog is global metadata — safe to cache for the session.
    staleTime: Infinity,
  });

export const findIntegration = (
  catalog: IntegrationCatalogResponse | undefined,
  key: string,
): IntegrationDefinition | undefined => catalog?.integrations.find((i) => i.key === key);
